import {
  Button,
  Card,
  Group,
  LoadingOverlay,
  Pagination,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconBox, IconPlus, IconSearch } from "@tabler/icons-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  ListError,
  ListHeader,
  ListScroll,
  ListSkeleton,
} from "@/components/DataList";
import { useAccess } from "@/hooks/useAccess";
import { lockProps } from "@/lib/permissions";
import { getCategories } from "@/api/category";
import {
  deleteProduct,
  getProducts,
  removeProductImage,
  updateProduct,
  updateProductRank,
  uploadProductImage,
} from "@/api/products";
import {
  deleteBranchOverride,
  getBranchOverrides,
  getBranches,
  upsertBranchOverride,
} from "@/api/branches";
import {
  getChannels,
  getConnections,
  getMappings,
  setMappingActive,
} from "@/api/connections";
import { notify } from "@/lib/notify";
import { PER_PAGE_OPTIONS } from "@/lib/format";
import { ImageUploadModal } from "@/components/ImageUploadModal";
import { IMAGE_TARGETS } from "@/lib/imageTargets";
import { PageTitle } from "@/components/PageTitle";
import { SortableProductRow } from "./SortableProductRow";
import { ProductFormModal } from "./ProductFormModal";
import { ProductIntegrationsModal } from "./ProductIntegrationsModal";
import { EMPTY_LIST } from "@/lib/queryClient";

// Başlık ve yükleme iskeleti aynı yerden besleniyor; genişlikler SortableProductRow'daki
// hücrelerle birebir örtüşmek zorunda, yoksa kolonlar kayar.
const PRODUCT_COLUMNS = [
  { spacer: true, w: 28 },
  { spacer: true, w: 40 },
  { label: "Ürün Adı", flex: true, ta: "left" },
  { label: "Fiyat", w: 90, ta: "right", sortKey: "basePrice" },
  { label: "Ent. Fiyat", w: 90, ta: "right", sortKey: "integrationPrice" },
  { label: "Durum", w: 70 },
  { label: "Şubeler", w: 90 },
  { label: "Ent. Durum", w: 90 },
  { label: "İşlem", w: 108 },
];

export default function Products() {
  const { can } = useAccess();
  const queryClient = useQueryClient();
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [items, setItems] = useState([]);
  const [togglingBranchId, setTogglingBranchId] = useState(null);

  const [formOpened, { open: openForm, close: closeForm }] =
    useDisclosure(false);
  const [formProduct, setFormProduct] = useState(null);
  const [imageOpened, { open: openImage, close: closeImage }] =
    useDisclosure(false);
  const [imageProduct, setImageProduct] = useState(null);
  const [
    integrationsOpened,
    { open: openIntegrations, close: closeIntegrations },
  ] = useDisclosure(false);
  const [integrationsProduct, setIntegrationsProduct] = useState(null);

  const { data: categories = EMPTY_LIST } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });
  const { data: branches = EMPTY_LIST } = useQuery({
    queryKey: ["branches"],
    queryFn: getBranches,
  });
  const { data: connections = EMPTY_LIST } = useQuery({
    queryKey: ["connections"],
    queryFn: getConnections,
  });
  const { data: channels = EMPTY_LIST } = useQuery({
    queryKey: ["channels"],
    queryFn: getChannels,
  });

  const overrideQueries = useQueries({
    queries: branches.map((branch) => ({
      queryKey: ["branch-overrides", branch.id],
      queryFn: () => getBranchOverrides(branch.id),
    })),
  });

  const overridesByBranch = useMemo(() => {
    const map = {};
    branches.forEach((branch, index) => {
      map[branch.id] = overrideQueries[index]?.data ?? [];
    });
    return map;
  }, [branches, overrideQueries]);

  const mappingQueries = useQueries({
    queries: connections.map((connection) => ({
      queryKey: ["mappings", connection.id],
      queryFn: () => getMappings(connection.id),
    })),
  });
  // Eşleştirmeler bağlantı başına ayrı sorgu; hepsi yerleşmeden "eşleşme yok" demek YANLIŞ olur.
  // Satırdaki entegrasyon ikonu bu yüzden pasif+"Eşleştirme yok" gösterip sonradan aktifleşiyordu.
  const mappingsLoading = mappingQueries.some((query) => query.isPending);

  // Per product: one entry per connection with its mapping (if matched). branchId/branchName let
  // the row group the list by branch (çok bağlantıda düz liste karışıyordu); channelName is the
  // per-row label inside a branch group.
  const integrationsFor = useMemo(() => {
    const channelById = new Map(channels.map((item) => [item.id, item.name]));
    const setActiveById = new Map(
      channels.map((item) => [item.id, Boolean(item.supportsSetActive)]),
    );
    const branchById = new Map(branches.map((item) => [item.id, item.name]));
    // Şubeler menüdeki sırasıyla (rank) geliyor; entegrasyon listesi de aynı sırayı izlesin,
    // şube içinde de kanal adına göre. Aksi halde sıra bağlantıların eklenme sırasına kalıyor.
    const branchOrder = new Map(
      branches.map((item, index) => [item.id, index]),
    );
    const ordered = [...connections].sort(
      (a, b) =>
        (branchOrder.get(a.branchId) ?? Infinity) -
          (branchOrder.get(b.branchId) ?? Infinity) ||
        (channelById.get(a.channelId) ?? "").localeCompare(
          channelById.get(b.channelId) ?? "",
          "tr",
        ),
    );
    const mappingsByConnection = {};
    connections.forEach((connection, index) => {
      mappingsByConnection[connection.id] = mappingQueries[index]?.data ?? [];
    });

    return (productId) =>
      ordered.map((connection) => ({
        connectionId: connection.id,
        branchId: connection.branchId,
        branchName: branchById.get(connection.branchId) ?? "?",
        channelName: channelById.get(connection.channelId) ?? "?",
        // Only marketplace channels can be toggled active/passive from our side.
        supportsSetActive: setActiveById.get(connection.channelId) ?? false,
        mapping:
          mappingsByConnection[connection.id].find(
            (mapping) => mapping.productId === productId,
          ) ?? null,
      }));
  }, [connections, channels, branches, mappingQueries]);

  const categoryOptions = useMemo(
    () => [
      { value: "all", label: "Tümü" },
      ...categories.map((item) => ({
        value: String(item.id),
        label: item.name,
      })),
    ],
    [categories],
  );

  // Default to the first category that actually has products once categories + products load (DB
  // returns categories rank-sorted). Leading empty categories are skipped so the page never opens on
  // an empty list; falls back to the first category if none have any product.
  const [categoryInitialized, setCategoryInitialized] = useState(false);

  useEffect(() => {
    if (categoryInitialized || !categories.length || !data) {
      return;
    }
    const productCategoryIds = new Set(
      data.map((product) => product.categoryId),
    );
    const firstWithProducts = categories.find((item) =>
      productCategoryIds.has(item.id),
    );
    setCategory(String((firstWithProducts ?? categories[0]).id));
    setCategoryInitialized(true);
  }, [categories, data, categoryInitialized]);

  useEffect(() => {
    if (!data) {
      return;
    }
    const scoped =
      category === "all"
        ? data
        : data.filter((product) => product.categoryId === Number(category));
    setItems([...scoped].sort((a, b) => a.rank - b.rank));
  }, [data, category]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Fiyata göre sıralarken sürükleme KAPALI: ekrandaki sıra artık rank değil, sürükleyip
  // bırakmak görünmeyen bir rank yazar ve liste yerinde durur gibi görünürdü.
  const sortable = !search.trim() && category !== "all" && !sort;

  const toggleSort = (key) =>
    setSort((current) => {
      if (current?.key !== key) return { key, dir: "asc" };
      if (current.dir === "asc") return { key, dir: "desc" };
      // Üçüncü tık elle sıraya döndürüyor; sürükleme yeniden açılıyor.
      return null;
    });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matched = term
      ? items.filter((product) => product.name.toLowerCase().includes(term))
      : items;

    if (!sort) {
      return matched;
    }

    const factor = sort.dir === "asc" ? 1 : -1;

    // Fiyatı olmayan ürün (entegrasyon fiyatı boş olabiliyor) iki yönde de SONA gidiyor:
    // null'ı sıfır saymak, fiyatsızları artan sıralamada en ucuzlar gibi gösteriyordu.
    return [...matched].sort((a, b) => {
      const left = a[sort.key];
      const right = b[sort.key];

      if (left == null || right == null) {
        return left == null ? (right == null ? 0 : 1) : -1;
      }

      return factor * (left - right);
    });
  }, [items, search, sort]);

  useEffect(() => {
    setPage(1);
  }, [category, search, perPage, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageItems = filtered.slice((page - 1) * perPage, page * perPage);

  const rankMutation = useMutation({
    mutationFn: updateProductRank,
    onSuccess: () => notify.success("Sıralama kaydedildi"),
    onError: () =>
      notify.error("Sıralama kaydedilemedi — liste eski sırasına döndü"),
    // Sıra yalnızca local state'te duruyordu: cache eski rank'leri tutmaya devam ettiği
    // için sayfadan çıkıp 30sn içinde dönünce kaydedilen sıra geri alınmış gibi görünürdü.
    // Hata durumunda da tazeliyoruz; DB'den gelen sıra UI'daki yalanı düzeltir.
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: ["products"] }),
  });

  const activeMutation = useMutation({
    mutationFn: ({ id, isActive }) => updateProduct(id, { isActive }),
    onSuccess: (_data, { name, isActive }) => {
      notify.success(`${name} ${isActive ? "aktif edildi" : "pasife alındı"}`);
    },
    // Switch iyimser olarak çevrildi; istek patlarsa geri alınmazsa kullanıcı ürünü kapattığını
    // sanır ama menüde satışta kalır.
    onError: () => notify.error("Durum güncellenemedi"),
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: ["products"] }),
  });

  const branchMutation = useMutation({
    mutationFn: async ({ product, branchId, visible }) => {
      const override = overridesByBranch[branchId]?.find(
        (item) => item.productId === product.id,
      );
      // A row exists only to override something, so dropping it is only safe once it holds
      // no prices — the old check ignored integrationPriceOverride and deleted it too.
      const holdsNoPrice =
        override &&
        override.priceOverride === null &&
        override.integrationPriceOverride === null;

      if (visible && holdsNoPrice) {
        return deleteBranchOverride(branchId, product.id);
      }

      // Send only the field being toggled. The API's upsert sets exactly what it receives,
      // so leaving the price columns out keeps them intact — re-sending a price read from
      // a still-loading or failed override query would write null over a real one.
      return upsertBranchOverride(branchId, product.id, { isActive: visible });
    },
    onSuccess: (_data, { product, branchId, visible }) => {
      const branchName =
        branches.find((branch) => branch.id === branchId)?.name ?? "";
      notify.success(
        `${branchName} şubesinde ${product.name} ${visible ? "satışa açıldı" : "kapatıldı"}`,
      );
      queryClient.invalidateQueries({
        queryKey: ["branch-overrides", branchId],
      });
    },
    onError: () => notify.error("Şube durumu güncellenemedi"),
    onSettled: () => setTogglingBranchId(null),
  });

  // Bütün şubelerde tek seferde aç/kapat. Her şube için tek-şube mantığını uygular (fiyat
  // override'ı olan satırlarda fiyatı korur, boşsa siler), sonra hepsini bir kez tazeler.
  const allBranchesMutation = useMutation({
    mutationFn: async ({ product, visible }) => {
      // allSettled: bir şube düşerse diğerleri yazılmış kalır, sonucu olduğu gibi bildiririz.
      const results = await Promise.allSettled(
        branches.map((branch) => {
          const override = overridesByBranch[branch.id]?.find(
            (item) => item.productId === product.id,
          );
          const holdsNoPrice =
            override &&
            override.priceOverride === null &&
            override.integrationPriceOverride === null;

          if (visible && (!override || holdsNoPrice)) {
            // Zaten görünür (satır yok) ya da fiyatsız override → override'ı sil.
            return override
              ? deleteBranchOverride(branch.id, product.id)
              : null;
          }
          return upsertBranchOverride(branch.id, product.id, {
            isActive: visible,
          });
        }),
      );

      return {
        failed: results.filter((item) => item.status === "rejected").length,
        total: results.length,
      };
    },
    onSuccess: ({ failed, total }, { product, visible }) => {
      const verb = visible ? "satışa açıldı" : "kapatıldı";

      if (failed) {
        notify.error(
          `${product.name}: ${total - failed}/${total} şubede ${verb}, ${failed} şube başarısız`,
        );
        return;
      }
      notify.success(`${product.name} tüm şubelerde ${verb}`);
    },
    onError: () => notify.error("Şube durumları güncellenemedi"),
    // Kısmi başarıda da tazele: yazılan şubelerin cache'i güncellenmezse UI eski hali gösterir.
    onSettled: () =>
      branches.forEach(
        (branch) =>
          void queryClient.invalidateQueries({
            queryKey: ["branch-overrides", branch.id],
          }),
      ),
  });

  const handleToggleAllBranches = (product, visible) =>
    allBranchesMutation.mutate({ product, visible });

  const mappingToggleMutation = useMutation({
    // Kanala gerçekten aktif/pasif isteği atar; DB'yi sonucuyla günceller.
    mutationFn: ({ connectionId, productId, isActive }) =>
      setMappingActive(connectionId, productId, isActive),
    onSuccess: (_data, { connectionId, label, product, isActive }) => {
      notify.success(
        `${label} entegrasyonunda ${product.name} ${isActive ? "açıldı" : "kapatıldı"}`,
      );
      queryClient.invalidateQueries({ queryKey: ["mappings", connectionId] });
    },
    onError: (error) =>
      notify.error(
        error.response?.data?.error ?? "Entegrasyon durumu güncellenemedi",
      ),
  });

  // Tüm kanallarda tek seferde aç/kapat. Yalnızca aktif/pasif destekleyen (pazaryeri)
  // eşleşmiş kanallara istek atar; POS kanalları atlanır.
  const allMappingsMutation = useMutation({
    mutationFn: async ({ product, isActive }) => {
      const targets = integrationsFor(product.id).filter(
        (item) => item.mapping?.remoteId && item.supportsSetActive,
      );
      return Promise.all(
        targets.map((item) =>
          setMappingActive(item.connectionId, product.id, isActive),
        ),
      );
    },
    onSuccess: (_data, { product, isActive }) => {
      notify.success(
        `${product.name} tüm entegrasyonlarda ${isActive ? "açıldı" : "kapatıldı"}`,
      );
      connections.forEach((connection) =>
        queryClient.invalidateQueries({
          queryKey: ["mappings", connection.id],
        }),
      );
    },
    onError: (error) =>
      notify.error(
        error.response?.data?.error ?? "Entegrasyon durumları güncellenemedi",
      ),
  });

  const handleToggleAllMappings = (product, isActive) =>
    allMappingsMutation.mutate({ product, isActive });

  // Tek şubenin entegrasyonlarını topluca aç/kapat. "Tümü" çok geniş, kanal kanal tıklamak çok
  // yavaş: pratikte istenen "bu ürünü şu şubede sat / satma". Aynı filtre — yalnızca eşleşmiş ve
  // aktif/pasif destekleyen kanallara istek gider.
  const branchMappingsMutation = useMutation({
    mutationFn: async ({ product, branchId, isActive }) => {
      const targets = integrationsFor(product.id).filter(
        (item) =>
          item.branchId === branchId &&
          item.mapping?.remoteId &&
          item.supportsSetActive,
      );
      return Promise.all(
        targets.map((item) =>
          setMappingActive(item.connectionId, product.id, isActive),
        ),
      );
    },
    onSuccess: (_data, { product, branchName, isActive }) => {
      notify.success(
        `${product.name} — ${branchName} entegrasyonlarında ${isActive ? "açıldı" : "kapatıldı"}`,
      );
      connections.forEach((connection) =>
        queryClient.invalidateQueries({
          queryKey: ["mappings", connection.id],
        }),
      );
    },
    onError: (error) =>
      notify.error(
        error.response?.data?.error ?? "Entegrasyon durumları güncellenemedi",
      ),
  });

  const handleToggleBranchMappings = (
    product,
    branchId,
    branchName,
    isActive,
  ) =>
    branchMappingsMutation.mutate({ product, branchId, branchName, isActive });

  // Ent. Durum / şube menülerindeki her düğme ve switch için "şu an bu mu işleniyor" bilgisi.
  // Hepsi tek nesnede toplanıyor; satır bileşeni yalnızca kendi ürününe ait olanı görüyor, yoksa
  // bir üründeki istek tüm listede spinner yakardı.
  const pendingFor = (productId) => {
    const mine = (mutation) =>
      mutation.isPending && mutation.variables?.product?.id === productId;

    return {
      // Ürünün genel aktif/pasif switch'i iyimser çevriliyor; spinner yeni durumu gizlerdi,
      // o yüzden yalnızca kilitlenir (uçuş sırasında ikinci tık yarış yaratmasın).
      active:
        activeMutation.isPending && activeMutation.variables?.id === productId,
      // Tek kanal switch'i — hangi bağlantı işleniyor.
      mappingConnectionId: mine(mappingToggleMutation)
        ? mappingToggleMutation.variables.connectionId
        : null,
      // Şube grubu toplu aç/kapat — hangi şube, hangi yön.
      branchMappings: mine(branchMappingsMutation)
        ? {
            branchId: branchMappingsMutation.variables.branchId,
            isActive: branchMappingsMutation.variables.isActive,
          }
        : null,
      // Global toplu aç/kapat — yalnızca yön (null = işlem yok).
      allMappings: mine(allMappingsMutation)
        ? allMappingsMutation.variables.isActive
        : null,
      allBranches: mine(allBranchesMutation)
        ? allBranchesMutation.variables.visible
        : null,
    };
  };

  const deleteMutation = useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      notify.success("Ürün silindi");
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: () => notify.error("Ürün silinemedi"),
  });

  const handleDragEnd = ({ active, over }) => {
    if (!sortable || !over || active.id === over.id) {
      return;
    }
    const oldIndex = items.findIndex((product) => product.id === active.id);
    const newIndex = items.findIndex((product) => product.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    rankMutation.mutate(
      reordered.map((product, index) => ({ id: product.id, rank: index })),
    );
  };

  const handleToggleActive = (product, isActive) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === product.id ? { ...item, isActive } : item,
      ),
    );
    activeMutation.mutate({ id: product.id, isActive, name: product.name });
  };

  const handleToggleBranch = (product, branchId, visible) => {
    setTogglingBranchId(branchId);
    branchMutation.mutate({ product, branchId, visible });
  };

  const handleToggleMapping = (product, integration, isActive) =>
    mappingToggleMutation.mutate({
      connectionId: integration.connectionId,
      productId: product.id,
      mapping: integration.mapping,
      label: `${integration.channelName} — ${integration.branchName}`,
      product,
      isActive,
    });

  const handleDelete = (product) =>
    modals.openConfirmModal({
      title: "Ürünü sil",
      centered: true,
      children: (
        <Text size="sm">
          {product.name} ürününü silmek istediğine emin misin?
        </Text>
      ),
      labels: { confirm: "Sil", cancel: "Vazgeç" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMutation.mutate(product.id),
    });

  const handleNew = () => {
    setFormProduct(null);
    openForm();
  };

  const handleEdit = (product) => {
    setFormProduct(product);
    openForm();
  };

  const handleEditImage = (product) => {
    setImageProduct(product);
    openImage();
  };

  const handleOpenIntegrations = (product) => {
    setIntegrationsProduct(product);
    openIntegrations();
  };

  return (
    <Stack gap="lg">
      <PageTitle>Ürünler</PageTitle>
      <Group justify="space-between">
        <Group>
          <ThemeIcon size={46} radius="md" variant="light" color="chocolate">
            <IconBox size={26} stroke={1.6} />
          </ThemeIcon>
          <div>
            <Title order={2}>Ürünler</Title>
            <Text c="dimmed" size="sm">
              Kategoriye göre listele, sürükleyerek sırala, şube görünürlüğünü
              yönet
            </Text>
          </div>
        </Group>
        {/* Yetkisiz kullanıcıda gizlenmiyor, kilitleniyor. */}
        <Button
          leftSection={<IconPlus size={16} />}
          {...lockProps(can("products"))}
          onClick={handleNew}
        >
          Yeni Ürün
        </Button>
      </Group>

      <Card withBorder radius="md" p="md">
        <Group justify="center" mb="md">
          <TextInput
            w={260}
            placeholder="Ürün adı ara"
            leftSection={<IconSearch size={15} />}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <Select
            aria-label="Kategoriye göre filtrele"
            w={240}
            searchable
            withCheckIcon={false}
            allowDeselect={false}
            data={categoryOptions}
            value={category}
            onChange={setCategory}
          />
        </Group>

        <Card withBorder radius="md" p={0} pos="relative">
          <LoadingOverlay
            visible={rankMutation.isPending}
            zIndex={5}
            overlayProps={{ blur: 1, backgroundOpacity: 0.35 }}
            loaderProps={{ size: "sm" }}
          />
          {isLoading ? (
            <ListScroll minWidth={860}>
              <ListHeader
                columns={PRODUCT_COLUMNS}
                sort={sort}
                onSort={toggleSort}
              />
              <ListSkeleton
                columns={PRODUCT_COLUMNS}
                rows={perPage > 10 ? 10 : perPage}
              />
            </ListScroll>
          ) : isError ? (
            <ListError title="Ürünler alınamadı" onRetry={refetch} />
          ) : (
            <>
              <ListScroll minWidth={860}>
                <ListHeader
                  columns={PRODUCT_COLUMNS}
                  sort={sort}
                  onSort={toggleSort}
                />

                {pageItems.length === 0 ? (
                  <Text c="dimmed" ta="center" py="xl">
                    Ürün bulunamadı
                  </Text>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={pageItems.map((product) => product.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {pageItems.map((product) => (
                        <SortableProductRow
                          key={product.id}
                          product={product}
                          branches={branches}
                          overridesByBranch={overridesByBranch}
                          sortable={sortable}
                          togglingBranchId={
                            branchMutation.isPending &&
                            branchMutation.variables?.product.id === product.id
                              ? togglingBranchId
                              : null
                          }
                          integrations={integrationsFor(product.id)}
                          integrationsLoading={mappingsLoading}
                          pending={pendingFor(product.id)}
                          onToggleActive={handleToggleActive}
                          onToggleBranch={handleToggleBranch}
                          onToggleAllBranches={handleToggleAllBranches}
                          onOpenIntegrations={handleOpenIntegrations}
                          onEdit={handleEdit}
                          onEditImage={handleEditImage}
                          onDelete={handleDelete}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </ListScroll>

              <Group justify="space-between" p="sm">
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    Sayfa başı
                  </Text>
                  <Select
                    withCheckIcon={false}
                    w={75}
                    size="xs"
                    data={PER_PAGE_OPTIONS}
                    value={String(perPage)}
                    onChange={(value) => {
                      setPerPage(Number(value));
                      setPage(1);
                    }}
                  />
                  <Text size="sm" c="dimmed">
                    {filtered.length} ürün
                  </Text>
                </Group>
                <Pagination
                  withEdges
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                  size="sm"
                />
              </Group>
            </>
          )}
        </Card>
      </Card>

      <ProductFormModal
        opened={formOpened}
        onClose={closeForm}
        product={formProduct}
        categories={categories}
      />
      {/* Ürün listeden gelmeye devam etsin: switch'ler değişince satır tazelenir, modal
                içindeki durum da onunla birlikte güncellenir. */}
      <ProductIntegrationsModal
        opened={integrationsOpened}
        onClose={closeIntegrations}
        product={integrationsProduct}
        integrations={
          integrationsProduct ? integrationsFor(integrationsProduct.id) : []
        }
        loading={mappingsLoading}
        pending={integrationsProduct ? pendingFor(integrationsProduct.id) : {}}
        onToggleMapping={handleToggleMapping}
        onToggleAllMappings={handleToggleAllMappings}
        onToggleBranchMappings={handleToggleBranchMappings}
      />
      <ImageUploadModal
        opened={imageOpened}
        onClose={closeImage}
        title="Ürün Görseli"
        idField="id"
        idValue={imageProduct?.id}
        uploadFn={uploadProductImage}
        currentImageUrl={imageProduct?.imageUrl}
        removeFn={removeProductImage}
        invalidateKey={["products"]}
        crop={IMAGE_TARGETS.product}
      />
    </Stack>
  );
}
